// Note: This is to keep parity with eedi-tutor without breaking eedi-web support (normal latex)
// We should normalize this some day

import type { FC } from "react";
import { assert } from "@/helpers/console.ts";
import { splitStringByNewline } from "@/helpers/markdown.ts";
import ReactMarkdown from "react-markdown";
import { RehypePlugins, RemarkPlugins } from "@/helpers/latex.tsx";

export function isLineEnding(str: string | undefined) {
  return isUnixLineEnding(str) || isWindowsLineEnding(str);
}

export function isWindowsLineEnding(str: string | undefined) {
  return str === "\r\n";
}

export function isUnixLineEnding(str: string | undefined) {
  return str === "\n";
}

const escapedCharacters: Record<string, string> = {
  "\b": "\\b",
  "\f": "\\f",
  "\n": "\\n",
  "\r": "\\r",
  "\t": "\\t",
  "\v": "\\v",
};

const blockElements = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "pre",
  "thead",
  "tbody",
  "tr",
  "td",
  "figure",
  "figcaption",
];

function replaceEscapedCharacters(text: string) {
  let escapedText = "";

  for (const character of text) {
    escapedText += escapedCharacters[character] ?? character;
  }

  return escapedText;
}

/**
 * Normalise math delimiters in flow markdown for remark-math/KaTeX.
 */
export function processMathFragments(text: string) {
  if (typeof text !== "string") {
    return "";
  }

  let output = text;

  output = output.replaceAll("$$$", "\\$$");
  output = output !== text ? output.replaceAll("$$", "") : text;

  const mathRegex = /(?:\$\$|\\\(|\\\[)(\r\n|\n)?(.+?)(\r\n|\n)?(?:\$\$|\\\)|\\])/gs;

  output = output.replaceAll(mathRegex, (_match, startClosing, content, endStarting) => {
    const startClosingValue = startClosing ?? "";
    const endStartingValue = endStarting ?? "";
    const isBlock = isLineEnding(startClosingValue) && isLineEnding(endStartingValue);
    const startDelimiterClosing = isBlock
      ? startClosingValue
      : replaceEscapedCharacters(startClosingValue);
    const startDelimiter = `$$${startDelimiterClosing}`;
    const endDelimiter = `${endStartingValue}$$`;
    const escapedContent = replaceEscapedCharacters(content);

    return `${startDelimiter}${escapedContent}${endDelimiter}`;
  });

  return output;
}

interface RenderLatex {
  message: string;
}

export const RenderTutorPairtyLatex: FC<RenderLatex> = ({ message }) => {
  assert(message.length !== 0, "Latex message length needs to be more than 0");

  const splitText = splitStringByNewline(processMathFragments(message));

  return (
    <>
      {splitText
        .filter((text) => text.length > 0)
        .map((text) => (
          <ReactMarkdown
            key={text}
            // @ts-expect-error
            remarkPlugins={RemarkPlugins}
            rehypePlugins={RehypePlugins}
            disallowedElements={blockElements}
            skipHtml={true}
          >
            {text}
          </ReactMarkdown>
        ))}
    </>
  );
};
